import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import CalendarView from './CalendarView';
import ScheduleModal from './ScheduleModal';
import DayView from './DayView';
import Settings from './Settings';
import MandateScheduleModal from './MandateScheduleModal';
import JobScheduleModal from './JobScheduleModal';
import toast from 'react-hot-toast';
import PageHeading from '../../components/PageHeading';
import { isJobProgressEvent } from '../../utils/scheduleEvents';

function Schedule() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState('month');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDayView, setShowDayView] = useState(false);

  const [mandateEvent, setMandateEvent] = useState(null);
  const [jobEvent, setJobEvent] = useState(null);

  const syncMutation = useMutation({
    mutationFn: () => api.post('/jobs/sync-schedules'),
    onSuccess: (res) => {
      const n = res?.data?.added ?? 0;
      const prep = res?.data?.notify?.prepare;
      const emailed = (prep?.emailed || 0) + (prep?.chased || 0);
      const created = prep?.created || 0;
      const parts = [];
      if (n > 0) parts.push(`${n} visit(s) synced`);
      if (created > 0) parts.push(`${created} Notified job(s) created`);
      if (emailed > 0) parts.push(`${emailed} email(s) to IQP/company`);
      toast.success(parts.length ? parts.join(' · ') : 'Schedule up to date');
      queryClient.invalidateQueries(['schedules']);
      queryClient.invalidateQueries(['job-schedule-events']);
      queryClient.invalidateQueries(['jobs']);
    },
    onError: () => toast.error('Failed to sync / notify'),
  });

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowDayView(true);
  };

  const handleEventClick = (event) => {
    if (event?.source === 'mandate') {
      setMandateEvent(event);
      return;
    }
    if (isJobProgressEvent(event)) {
      setJobEvent(event);
      return;
    }
    setSelectedEvent(event);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
    setSelectedDate(null);
  };

  const handleCloseDayView = () => {
    setShowDayView(false);
    setSelectedDate(null);
  };

  return (
    <div>
      <PageHeading
        title="Schedule"
        infoTitle="Schedule"
        infoChildren={
          <>
            <p>
              When an SS inspection enters its lead-time window, Sync &amp; notify creates a <strong>Notified</strong>{' '}
              job on this calendar and emails the internal IQP and related company at the same time. Click a Notify
              chip to see who was contacted. Confirmed dates become Scheduled visits.
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Notice before due: 1m → 2 weeks · 3m → 1 month · 6m → 1 month · 12m → 2 months. Visit remind after
              booking: 1m → 1 week · 3m/6m → 2 weeks · 12m → 1 month (sooner if booked inside the window). Daily
              chase until a date is proposed.
            </p>
          </>
        }
        subPages={[
          { id: 'add', label: 'Add Schedule', onClick: () => setShowModal(true) },
          { id: 'settings', label: 'Settings', onClick: () => setShowSettings(true) },
          ...(user?.role !== 'WORKER'
            ? [
                {
                  id: 'sync',
                  label: syncMutation.isLoading ? 'Syncing…' : 'Sync & notify due',
                  onClick: () => {
                    if (!syncMutation.isLoading) syncMutation.mutate();
                  },
                },
              ]
            : []),
        ]}
      />

      {showDayView && selectedDate ? (
        <DayView
          date={selectedDate}
          onClose={handleCloseDayView}
          onEventClick={handleEventClick}
        />
      ) : (
        <CalendarView
          viewMode={viewMode}
          onViewChange={setViewMode}
          onDateClick={handleDateClick}
          onEventClick={handleEventClick}
        />
      )}

      {showModal && (
        <ScheduleModal
          event={selectedEvent}
          defaultDate={selectedDate}
          onClose={handleCloseModal}
        />
      )}

      {mandateEvent && (
        <MandateScheduleModal event={mandateEvent} onClose={() => setMandateEvent(null)} />
      )}

      {jobEvent && <JobScheduleModal event={jobEvent} onClose={() => setJobEvent(null)} />}

      {showSettings && (
        <Settings onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}

export default Schedule;
